export const DEFAULT_NODE_SIZE = Object.freeze({ width: 260, height: 110 });
export const DEFAULT_LAYOUT_GAP = Object.freeze({ horizontal: 80, vertical: 32 });
export const DEFAULT_COLLISION_PADDING = 16;

function finite(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

export function getNodeSize(node) {
  const candidate = node?.layoutSize || node?.size || {};
  return {
    width: Math.max(1, finite(candidate.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(1, finite(candidate.height, DEFAULT_NODE_SIZE.height)),
  };
}

export function topLeftRectsOverlap(a, b, padding = 0) {
  return !(
    a.x + a.width + padding <= b.x ||
    b.x + b.width + padding <= a.x ||
    a.y + a.height + padding <= b.y ||
    b.y + b.height + padding <= a.y
  );
}

function nodeRect(node, nodeSizes = null) {
  const size = getRuntimeNodeSize(node, nodeSizes);
  return {
    x: finite(node?.position?.x, 0),
    y: finite(node?.position?.y, 0),
    ...size,
  };
}

function findParentId(nodeId, relationships) {
  return relationships.find((relationship) => relationship.childId === nodeId)?.parentId || null;
}

function getPinnedLayout(node, nodesById, relationships) {
  const existingLayout = node?.layout || {};
  const parentId = findParentId(node.id, relationships);
  const parent = nodesById.get(parentId);
  const nodeCenterX = finite(node?.position?.x, 0) + getNodeSize(node).width / 2;
  const parentCenterX = parent
    ? finite(parent.position?.x, 0) + getNodeSize(parent).width / 2
    : 0;
  const side = existingLayout.side === 'left' || existingLayout.side === 'right'
    ? existingLayout.side
    : (parent && nodeCenterX < parentCenterX ? 'left' : 'right');
  const siblingIds = relationships
    .filter((relationship) => relationship.parentId === parentId)
    .map((relationship) => relationship.childId)
    .sort((leftId, rightId) => {
      const left = nodesById.get(leftId);
      const right = nodesById.get(rightId);
      const leftOrder = finite(left?.layout?.order, Infinity);
      const rightOrder = finite(right?.layout?.order, Infinity);
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return finite(left?.position?.y, 0) - finite(right?.position?.y, 0)
        || String(leftId).localeCompare(String(rightId));
    });
  const inferredOrder = Math.max(0, siblingIds.indexOf(node.id));

  return {
    ...existingLayout,
    side,
    order: Math.max(0, finite(existingLayout.order, inferredOrder)),
    pinned: true,
  };
}

export function getSubtreeIds(nodeId, relationships) {
  if (!nodeId) return [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const childrenByParent = new Map();

  safeRelationships.forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });

  childrenByParent.forEach((children) => {
    children.sort((left, right) => String(left).localeCompare(String(right)));
  });

  const result = [];
  const visited = new Set();
  const pending = [nodeId];

  while (pending.length > 0) {
    const current = pending.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);
    result.push(current);
    pending.push(...(childrenByParent.get(current) || []));
  }

  return result;
}

export function buildMoveSubtreeCommand({
  nodeId,
  targetPosition,
  nodes,
  relationships,
  pinRoot = false,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const rootNode = nodesById.get(nodeId);
  if (!rootNode) throw new Error(`Node ${nodeId} does not exist`);
  if (!Number.isFinite(targetPosition?.x) || !Number.isFinite(targetPosition?.y)) {
    throw new Error('A finite target position is required');
  }

  const sourcePosition = {
    x: finite(rootNode.position?.x, 0),
    y: finite(rootNode.position?.y, 0),
  };
  const delta = {
    x: targetPosition.x - sourcePosition.x,
    y: targetPosition.y - sourcePosition.y,
  };
  const positions = getSubtreeIds(nodeId, safeRelationships)
    .map((id) => nodesById.get(id))
    .filter(Boolean)
    .map((node) => ({
      id: node.id,
      position: {
        x: finite(node.position?.x, 0) + delta.x,
        y: finite(node.position?.y, 0) + delta.y,
      },
      ...(pinRoot && node.id === nodeId ? {
        layout: getPinnedLayout(node, nodesById, safeRelationships),
      } : {}),
    }));

  return {
    type: 'move-subtree',
    nodeId,
    delta,
    positions,
  };
}

function getRuntimeNodeSize(node, nodeSizes) {
  const runtimeSize = nodeSizes instanceof Map
    ? nodeSizes.get(node.id)
    : nodeSizes?.[node.id];
  return runtimeSize ? getNodeSize({ ...node, layoutSize: runtimeSize }) : getNodeSize(node);
}

function commandHasCollision(command, nodes, nodeSizes, padding, offset) {
  const movingIds = new Set(command.positions.map((entry) => entry.id));
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const stationaryRects = nodes
    .filter((node) => !movingIds.has(node.id))
    .map((node) => nodeRect(node, nodeSizes));

  return command.positions.some((entry) => {
    const node = nodesById.get(entry.id);
    if (!node) return false;
    const size = getRuntimeNodeSize(node, nodeSizes);
    const movedRect = {
      x: entry.position.x + offset.x,
      y: entry.position.y + offset.y,
      ...size,
    };
    return stationaryRects.some((rect) => topLeftRectsOverlap(movedRect, rect, padding));
  });
}

export function resolveMoveCommandCollisions(command, {
  nodes,
  nodeSizes = null,
  padding = DEFAULT_COLLISION_PADDING,
  step = 16,
  maxRings = 80,
} = {}) {
  if (!['move-subtree', 'reparent-subtree'].includes(command?.type) || !Array.isArray(command.positions)) {
    throw new Error('Invalid position command');
  }
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safePadding = Math.max(0, finite(padding, DEFAULT_COLLISION_PADDING));
  const safeStep = Math.max(1, finite(step, 16));

  const collisionFreeAt = (offset) => !commandHasCollision(
    command,
    safeNodes,
    nodeSizes,
    safePadding,
    offset
  );

  let resolvedOffset = { x: 0, y: 0 };
  if (!collisionFreeAt(resolvedOffset)) {
    resolvedOffset = null;
    for (let ring = 1; ring <= maxRings && !resolvedOffset; ring += 1) {
      const distance = ring * safeStep;
      const candidates = [
        { x: 0, y: distance },
        { x: 0, y: -distance },
        { x: distance, y: 0 },
        { x: -distance, y: 0 },
      ];
      for (let index = 1; index < ring; index += 1) {
        const minor = index * safeStep;
        candidates.push(
          { x: minor, y: distance },
          { x: -minor, y: distance },
          { x: minor, y: -distance },
          { x: -minor, y: -distance },
          { x: distance, y: minor },
          { x: distance, y: -minor },
          { x: -distance, y: minor },
          { x: -distance, y: -minor },
        );
      }
      resolvedOffset = candidates.find(collisionFreeAt) || null;
    }
  }

  if (!resolvedOffset) throw new Error('Unable to resolve node collision');
  if (resolvedOffset.x === 0 && resolvedOffset.y === 0) return command;

  return {
    ...command,
    collisionResolved: true,
    collisionOffset: resolvedOffset,
    delta: {
      x: command.delta.x + resolvedOffset.x,
      y: command.delta.y + resolvedOffset.y,
    },
    positions: command.positions.map((entry) => ({
      ...entry,
      position: {
        x: entry.position.x + resolvedOffset.x,
        y: entry.position.y + resolvedOffset.y,
      },
    })),
  };
}

export function buildReparentSubtreeCommand({
  nodeId,
  newParentId,
  nodes,
  relationships,
  nodeSizes = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  const newParent = nodesById.get(newParentId);
  if (!node) throw new Error(`Node ${nodeId} does not exist`);
  if (!newParent) throw new Error(`Parent node ${newParentId} does not exist`);
  if (nodeId === newParentId) throw new Error('A node cannot be its own parent');

  const previousParentId = findParentId(nodeId, safeRelationships);
  if (!previousParentId) throw new Error('The root node cannot be reparented');
  if (previousParentId === newParentId) throw new Error('Node already belongs to that parent');

  const subtreeIds = new Set(getSubtreeIds(nodeId, safeRelationships));
  if (subtreeIds.has(newParentId)) throw new Error('Reparenting would create a cycle');

  const stationaryNodes = safeNodes.filter((candidate) => !subtreeIds.has(candidate.id));
  const stationaryRelationships = safeRelationships.filter(
    (relationship) => !subtreeIds.has(relationship.parentId) && !subtreeIds.has(relationship.childId)
  );
  const targetLayout = planChildLayout({
    parentId: newParentId,
    nodes: stationaryNodes,
    relationships: stationaryRelationships,
    size: getRuntimeNodeSize(node, nodeSizes),
    nodeSizes,
  });
  const movement = buildMoveSubtreeCommand({
    nodeId,
    targetPosition: targetLayout.position,
    nodes: safeNodes,
    relationships: safeRelationships,
  });
  const command = {
    ...movement,
    type: 'reparent-subtree',
    positions: movement.positions.map((entry) => (
      entry.id === nodeId
        ? { ...entry, layout: targetLayout.layout }
        : entry
    )),
    previousParentId,
    newParentId,
    removeRelationship: { parentId: previousParentId, childId: nodeId },
    relationship: { parentId: newParentId, childId: nodeId },
  };

  return resolveMoveCommandCollisions(command, {
    nodes: safeNodes,
    nodeSizes,
    padding: DEFAULT_LAYOUT_GAP.vertical,
  });
}

function inferBranchSide(parent, nodesById, relationships, children) {
  if (parent?.layout?.side === 'left') return -1;
  if (parent?.layout?.side === 'right') return 1;
  if (children.length > 0) {
    const averageChildX = children.reduce(
      (sum, child) => sum + finite(child.position?.x, 0),
      0
    ) / children.length;
    return averageChildX < finite(parent.position?.x, 0) ? -1 : 1;
  }

  const grandparent = nodesById.get(findParentId(parent.id, relationships));
  if (grandparent) {
    return finite(parent.position?.x, 0) < finite(grandparent.position?.x, 0) ? -1 : 1;
  }

  return 1;
}

function centerOfNode(node, nodeSizes = null) {
  const size = getRuntimeNodeSize(node, nodeSizes);
  return {
    x: finite(node?.position?.x, 0) + size.width / 2,
    y: finite(node?.position?.y, 0) + size.height / 2,
  };
}

function normalizeVector(vector, fallback = { x: 1, y: 0 }) {
  const length = Math.hypot(vector?.x || 0, vector?.y || 0);
  if (length < 0.001) return fallback;
  return { x: vector.x / length, y: vector.y / length };
}

function crossProduct(a, b, c) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function pointsEqual(a, b, epsilon = 0.001) {
  return Math.abs(a.x - b.x) <= epsilon && Math.abs(a.y - b.y) <= epsilon;
}

function pointOnSegment(point, start, end, epsilon = 0.001) {
  return Math.abs(crossProduct(start, end, point)) <= epsilon
    && point.x >= Math.min(start.x, end.x) - epsilon
    && point.x <= Math.max(start.x, end.x) + epsilon
    && point.y >= Math.min(start.y, end.y) - epsilon
    && point.y <= Math.max(start.y, end.y) + epsilon;
}

function segmentsCross(startA, endA, startB, endB) {
  const sharedOrigin = pointsEqual(startA, startB) || pointsEqual(startA, endB);
  if (sharedOrigin) {
    const otherEnd = pointsEqual(startA, startB) ? endB : startB;
    const collinear = Math.abs(crossProduct(startA, endA, otherEnd)) <= 0.001;
    if (!collinear) return false;

    const dot = (otherEnd.x - startA.x) * (endA.x - startA.x)
      + (otherEnd.y - startA.y) * (endA.y - startA.y);
    return dot > 0.001;
  }

  const a = crossProduct(startA, endA, startB);
  const b = crossProduct(startA, endA, endB);
  const c = crossProduct(startB, endB, startA);
  const d = crossProduct(startB, endB, endA);
  if (((a > 0 && b < 0) || (a < 0 && b > 0))
    && ((c > 0 && d < 0) || (c < 0 && d > 0))) {
    return true;
  }

  return (Math.abs(a) <= 0.001 && pointOnSegment(startB, startA, endA))
    || (Math.abs(b) <= 0.001 && pointOnSegment(endB, startA, endA))
    || (Math.abs(c) <= 0.001 && pointOnSegment(startA, startB, endB))
    || (Math.abs(d) <= 0.001 && pointOnSegment(endA, startB, endB));
}

function segmentCrossesRect(start, end, rect, padding = 0) {
  const expanded = {
    x: rect.x - padding,
    y: rect.y - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
  const corners = [
    { x: expanded.x, y: expanded.y },
    { x: expanded.x + expanded.width, y: expanded.y },
    { x: expanded.x + expanded.width, y: expanded.y + expanded.height },
    { x: expanded.x, y: expanded.y + expanded.height },
  ];
  const isInside = (point) => (
    point.x >= expanded.x
    && point.x <= expanded.x + expanded.width
    && point.y >= expanded.y
    && point.y <= expanded.y + expanded.height
  );

  return isInside(start)
    || isInside(end)
    || corners.some((corner, index) => (
      segmentsCrossesWithoutSharedEndpoint(
        start,
        end,
        corner,
        corners[(index + 1) % corners.length]
      )
    ));
}

function segmentsCrossesWithoutSharedEndpoint(startA, endA, startB, endB) {
  const a = crossProduct(startA, endA, startB);
  const b = crossProduct(startA, endA, endB);
  const c = crossProduct(startB, endB, startA);
  const d = crossProduct(startB, endB, endA);

  if (((a > 0 && b < 0) || (a < 0 && b > 0))
    && ((c > 0 && d < 0) || (c < 0 && d > 0))) {
    return true;
  }
  return (Math.abs(a) <= 0.001 && pointOnSegment(startB, startA, endA))
    || (Math.abs(b) <= 0.001 && pointOnSegment(endB, startA, endA))
    || (Math.abs(c) <= 0.001 && pointOnSegment(startA, startB, endB))
    || (Math.abs(d) <= 0.001 && pointOnSegment(endA, startB, endB));
}

function inferChildDirection(parent, nodesById, relationships, children, nodeSizes = null) {
  const parentCenter = centerOfNode(parent, nodeSizes);
  const grandparent = nodesById.get(findParentId(parent.id, relationships));
  if (grandparent) {
    const grandparentCenter = centerOfNode(grandparent, nodeSizes);
    return normalizeVector({
      x: parentCenter.x - grandparentCenter.x,
      y: parentCenter.y - grandparentCenter.y,
    });
  }

  return { x: inferBranchSide(parent, nodesById, relationships, children), y: 0 };
}

function findAvailablePosition({
  parent,
  preferred,
  direction,
  size,
  nodes,
  relationships,
  gap,
  nodeSizes = null,
}) {
  const existingRects = nodes.map((node) => nodeRect(node, nodeSizes));
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const parentCenter = centerOfNode(parent, nodeSizes);
  const parentSize = getRuntimeNodeSize(parent, nodeSizes);
  const unitDirection = normalizeVector(direction);
  const perpendicular = { x: -unitDirection.y, y: unitDirection.x };
  const supportDistance = (
    Math.abs(unitDirection.x) * (parentSize.width + size.width) / 2
    + Math.abs(unitDirection.y) * (parentSize.height + size.height) / 2
    + gap.horizontal
  );
  const preferredCenter = preferred
    ? { x: preferred.x + size.width / 2, y: preferred.y + size.height / 2 }
    : null;
  const preferredDistance = preferredCenter
    ? Math.max(
      supportDistance,
      (preferredCenter.x - parentCenter.x) * unitDirection.x
        + (preferredCenter.y - parentCenter.y) * unitDirection.y
    )
    : supportDistance;
  const perpendicularStep = (
    Math.abs(perpendicular.x) * (parentSize.width + size.width) / 2
    + Math.abs(perpendicular.y) * (parentSize.height + size.height) / 2
    + gap.vertical
  );
  const radialStep = Math.max(16, gap.vertical);
  const candidates = [];
  const addCandidate = (position, lane, radialRing = 0, orderPenalty = 0) => {
    const center = {
      x: position.x + size.width / 2,
      y: position.y + size.height / 2,
    };
    const along = (center.x - parentCenter.x) * unitDirection.x
      + (center.y - parentCenter.y) * unitDirection.y;
    const across = (center.x - parentCenter.x) * perpendicular.x
      + (center.y - parentCenter.y) * perpendicular.y;
    const distance = Math.hypot(center.x - parentCenter.x, center.y - parentCenter.y);
    const anglePenalty = Math.abs(Math.atan2(across, Math.max(0.001, along))) * 180;
    candidates.push({
      lane,
      radialRing,
      score: distance + anglePenalty + orderPenalty,
      position,
    });
  };

  for (let radialRing = 0; radialRing <= 32; radialRing += 1) {
    for (let lane = -16; lane <= 16; lane += 1) {
      const laneOffset = lane * perpendicularStep;
      const radialOffset = radialRing * radialStep;
      const center = {
        x: parentCenter.x + unitDirection.x * (preferredDistance + radialOffset)
          + perpendicular.x * laneOffset,
        y: parentCenter.y + unitDirection.y * (preferredDistance + radialOffset)
          + perpendicular.y * laneOffset,
      };
      addCandidate({
        x: center.x - size.width / 2,
        y: center.y - size.height / 2,
      }, lane, radialRing);
    }
  }

  const idealPosition = {
    x: parentCenter.x + unitDirection.x * preferredDistance - size.width / 2,
    y: parentCenter.y + unitDirection.y * preferredDistance - size.height / 2,
  };
  nodes.forEach((node) => {
    if (node.id === parent.id) return;
    const rect = nodeRect(node, nodeSizes);
    if (Math.abs(unitDirection.x) >= Math.abs(unitDirection.y)) {
      addCandidate({
        x: idealPosition.x,
        y: rect.y + rect.height + gap.vertical,
      }, 1);
      addCandidate({
        x: idealPosition.x,
        y: rect.y - size.height - gap.vertical,
      }, -1, 0, gap.vertical * 4);
    } else {
      addCandidate({
        x: rect.x + rect.width + gap.vertical,
        y: idealPosition.y,
      }, -1);
      addCandidate({
        x: rect.x - size.width - gap.vertical,
        y: idealPosition.y,
      }, 1);
    }
  });

  candidates.sort((left, right) => (
    left.score - right.score
    || Math.abs(left.lane) - Math.abs(right.lane)
    || right.lane - left.lane
    || left.radialRing - right.radialRing
  ));

  for (const plannedCandidate of candidates) {
    const candidate = {
      x: plannedCandidate.position.x,
      y: plannedCandidate.position.y,
    };
    const candidateRect = { ...candidate, ...size };
    if (existingRects.some((rect) => topLeftRectsOverlap(candidateRect, rect, gap.vertical))) continue;

    const candidateCenter = {
      x: candidate.x + size.width / 2,
      y: candidate.y + size.height / 2,
    };
    const crossesNode = nodes.some((node) => (
      node.id !== parent.id
      && segmentCrossesRect(parentCenter, candidateCenter, nodeRect(node, nodeSizes), 2)
    ));
    if (crossesNode) continue;

    const crossesEdge = relationships.some((relationship) => {
      const edgeParent = nodesById.get(relationship.parentId);
      const edgeChild = nodesById.get(relationship.childId);
      if (!edgeParent || !edgeChild) return false;
      return segmentsCross(
        parentCenter,
        candidateCenter,
        centerOfNode(edgeParent, nodeSizes),
        centerOfNode(edgeChild, nodeSizes)
      );
    });
    if (!crossesEdge) return candidate;
  }

  throw new Error('Unable to place child without overlap');
}

export function planChildLayout({
  parentId,
  nodes,
  relationships,
  preferredPosition = null,
  size = DEFAULT_NODE_SIZE,
  gap = DEFAULT_LAYOUT_GAP,
  nodeSizes = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const parent = nodesById.get(parentId);
  if (!parent) throw new Error(`Parent node ${parentId} does not exist`);

  const childIds = safeRelationships
    .filter((relationship) => relationship.parentId === parentId)
    .map((relationship) => relationship.childId);
  const children = childIds.map((id) => nodesById.get(id)).filter(Boolean);
  const parentSize = getRuntimeNodeSize(parent, nodeSizes);
  const childSize = {
    width: Math.max(1, finite(size.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(1, finite(size.height, DEFAULT_NODE_SIZE.height)),
  };

  const preferred = preferredPosition;
  const parentCenter = centerOfNode(parent, nodeSizes);
  const direction = preferred
    ? normalizeVector({
      x: finite(preferred.x, 0) + childSize.width / 2 - parentCenter.x,
      y: finite(preferred.y, 0) + childSize.height / 2 - parentCenter.y,
    })
    : inferChildDirection(parent, nodesById, safeRelationships, children, nodeSizes);

  const position = findAvailablePosition({
    parent,
    preferred: preferred
      ? { x: finite(preferred.x, 0), y: finite(preferred.y, 0) }
      : null,
    direction,
    size: childSize,
    nodes: safeNodes,
    relationships: safeRelationships,
    gap,
    nodeSizes,
  });
  const childCenterX = position.x + childSize.width / 2;
  const parentCenterX = finite(parent.position?.x, 0) + parentSize.width / 2;
  const side = childCenterX < parentCenterX ? -1 : 1;

  const highestOrder = children.reduce(
    (highest, child) => Math.max(highest, finite(child?.layout?.order, -1)),
    -1
  );
  return {
    position,
    layoutSize: childSize,
    layout: {
      side: side < 0 ? 'left' : 'right',
      order: Math.max(children.length, highestOrder + 1),
      pinned: false,
    },
  };
}

export function planChildPosition(options) {
  return planChildLayout(options).position;
}

export function buildCreateChildCommand({
  id,
  parentId,
  nodes,
  relationships,
  preferredPosition = null,
  size = DEFAULT_NODE_SIZE,
  nodeSizes = null,
  color = null,
  userId = null,
  creatorName = null,
}) {
  if (!id) throw new Error('A child node id is required');

  const planned = planChildLayout({
    parentId,
    nodes,
    relationships,
    preferredPosition,
    size,
    nodeSizes,
  });

  return {
    type: 'create-child',
    node: {
      id,
      text: '',
      position: planned.position,
      layoutSize: planned.layoutSize,
      layout: planned.layout,
      color,
      userId,
      creatorName,
      isRoot: false,
      collapsedChildren: null,
      embeddedMindmapId: null,
      linkUrl: null,
      linkMeta: null,
      isTask: false,
      taskDone: false,
      emoji: null,
      isEmbeddedMindmapLink: false,
      autoFocus: true,
    },
    relationship: { parentId, childId: id },
  };
}

export function buildCreateTaskChildCommand(options) {
  const command = buildCreateChildCommand(options);
  return {
    ...command,
    type: 'create-task-child',
    node: {
      ...command.node,
      isTask: true,
      taskDone: false,
    },
  };
}

export function buildCreateSiblingCommand({ selectedNodeId, ...options }) {
  const safeNodes = Array.isArray(options.nodes) ? options.nodes : [];
  const safeRelationships = Array.isArray(options.relationships) ? options.relationships : [];
  const selectedNode = safeNodes.find((node) => node.id === selectedNodeId);
  if (!selectedNode) throw new Error(`Selected node ${selectedNodeId} does not exist`);

  const parentId = findParentId(selectedNodeId, safeRelationships);
  if (!parentId) throw new Error('The root node cannot have a sibling');

  const command = buildCreateChildCommand({
    ...options,
    parentId,
    nodes: safeNodes,
    relationships: safeRelationships,
    color: options.color ?? selectedNode.color ?? null,
  });

  return { ...command, type: 'create-sibling' };
}

export function buildCreateLinkedMapCommand({
  linkedMindmapId,
  linkedMindmapUrl,
  title = 'New Map',
  ...options
}) {
  if (!linkedMindmapId) throw new Error('A linked mind map id is required');
  if (!linkedMindmapUrl) throw new Error('A linked mind map URL is required');

  const command = buildCreateChildCommand(options);
  const displayTitle = String(title || '').trim() || 'New Map';
  return {
    ...command,
    type: 'create-linked-map',
    node: {
      ...command.node,
      text: displayTitle,
      autoFocus: false,
      embeddedMindmapId: linkedMindmapId,
      isEmbeddedMindmapLink: true,
      linkUrl: linkedMindmapUrl,
      linkMeta: {
        url: linkedMindmapUrl,
        title: displayTitle,
        description: null,
        image: null,
        siteName: 'Mindmappi',
      },
    },
  };
}

export function buildDeleteSubtreeCommand({
  nodeId,
  rootId,
  nodes,
  relationships,
  actorUserId = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  if (!node) throw new Error(`Node ${nodeId} does not exist`);
  if (nodeId === rootId || node.isRoot) throw new Error('The root node cannot be deleted');

  const nodeIds = getSubtreeIds(nodeId, safeRelationships);
  const subtreeNodes = nodeIds.map((id) => nodesById.get(id));
  if (subtreeNodes.some((candidate) => !candidate)) {
    throw new Error('The branch contains a relationship to a missing node');
  }
  if (nodeIds.includes(rootId) || subtreeNodes.some((candidate) => candidate.isRoot)) {
    throw new Error('The branch is cyclic and would delete the root node');
  }

  if (actorUserId) {
    const foreignNode = subtreeNodes.find(
      (candidate) => candidate.userId && candidate.userId !== actorUserId
    );
    if (foreignNode) {
      throw new Error('This branch contains nodes created by another collaborator');
    }
  }

  const parentRelationships = safeRelationships.filter(
    (relationship) => relationship.childId === nodeId
  );
  if (parentRelationships.length !== 1) {
    throw new Error('The branch does not have exactly one parent relationship');
  }

  const deletingIds = new Set(nodeIds);
  const removedRelationships = safeRelationships.filter((relationship) => (
    deletingIds.has(relationship.parentId) || deletingIds.has(relationship.childId)
  ));
  const parentId = parentRelationships[0].parentId;

  return {
    type: 'delete-subtree',
    nodeId,
    nodeIds,
    parentId,
    nextSelectedId: parentId || rootId || null,
    removedRelationships,
  };
}
